import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class DashboardErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("CRASH ERROR:", error.message);
    console.error("CRASH COMPONENT STACK:", info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="max-w-lg w-full space-y-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-destructive mb-2">
                Something went wrong loading the dashboard
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {this.state.error?.message ?? "Unknown error"}
              </p>
              {this.state.componentStack && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">
                    Component stack (for debugging)
                  </summary>
                  <pre className="mt-2 overflow-auto max-h-48 text-[10px] bg-muted p-2 rounded">
                    {this.state.componentStack}
                  </pre>
                </details>
              )}
              <button
                className="mt-4 px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
